import { z } from 'zod';
import { IsoDatetimeToDateSchema } from './date-codecs.js';

type Optional<T> = T | undefined;

export const TwitterApiTweetAuthorSchema = z.object({
  id: z.string(),
  username: z.string(),
  name: z.string(),
  profileImageUrl: z.string().nullable().optional(),
  verifiedType: z.enum(['none', 'blue', 'business', 'government']),
  createdAt: IsoDatetimeToDateSchema,
  description: z.string().optional(),
  publicMetrics: z
    .object({
      followersCount: z.number(),
      followingCount: z.number(),
      tweetCount: z.number(),
      listedCount: z.number(),
    })
    .optional(),
  verified: z.boolean().optional(),
});

export type TwitterApiTweetAuthor = z.infer<typeof TwitterApiTweetAuthorSchema>;

const ReferencedTweetSchema = z
  .object({
    id: z.string(),
    type: z.string().optional(),
  })
  .passthrough();

export type TwitterApiReferencedTweet = z.infer<typeof ReferencedTweetSchema>;

export type TwitterApiTweet = {
  id: string;
  text: string;
  authorId: string;
  createdAt: Date;
  likeCount: number;
  retweetCount: number;
  replyCount: number;
  quoteCount: number;
  bookmarkCount?: Optional<number>;
  impressionCount?: Optional<number>;
  author?: Optional<TwitterApiTweetAuthor>;
  conversationId?: Optional<string>;
  inReplyToUserId?: Optional<string>;
  lang?: Optional<string>;
  entities?: Optional<unknown>;
  attachments?: Optional<unknown>;
  mediaItems?: Optional<unknown[]>;
  referencedTweets?: Optional<TwitterApiReferencedTweet[]>;
  quotedTweet?: Optional<TwitterApiTweet>;
  retweetedTweet?: Optional<TwitterApiTweet>;
  isLiked?: Optional<boolean>;
  isRetweeted?: Optional<boolean>;
  isLikedByUser?: Optional<boolean>;
  isPinned?: Optional<boolean>;
  relevanceScore?: Optional<number>;
  editHistoryTweetIds?: Optional<string[]>;
  contextAnnotations?: Optional<unknown[]>;
  possiblySensitive?: Optional<boolean>;
  replySettings?: Optional<string>;
  metadata?: Optional<Record<string, unknown>>;
  mediaMetadata?: Optional<unknown[]>;
};

const TwitterApiTweetSchemaInternal: z.ZodType<TwitterApiTweet> = z.lazy(() =>
  z
    .object({
      id: z.string(),
      text: z.string(),
      authorId: z.string(),
      createdAt: IsoDatetimeToDateSchema,
      likeCount: z.number(),
      retweetCount: z.number(),
      replyCount: z.number(),
      quoteCount: z.number(),
      bookmarkCount: z.number().optional(),
      impressionCount: z.number().optional(),
      author: TwitterApiTweetAuthorSchema.optional(),
      conversationId: z.string().optional(),
      inReplyToUserId: z.string().optional(),
      lang: z.string().optional(),
      entities: z.unknown().optional(),
      attachments: z.unknown().optional(),
      mediaItems: z.array(z.unknown()).optional(),
      referencedTweets: z.array(ReferencedTweetSchema).optional(),
      quotedTweet: TwitterApiTweetSchemaInternal.optional(),
      retweetedTweet: TwitterApiTweetSchemaInternal.optional(),
      isLiked: z.boolean().optional(),
      isRetweeted: z.boolean().optional(),
      isLikedByUser: z.boolean().optional(),
      isPinned: z.boolean().optional(),
      relevanceScore: z.number().optional(),
      editHistoryTweetIds: z.array(z.string()).optional(),
      contextAnnotations: z.array(z.unknown()).optional(),
      possiblySensitive: z.boolean().optional(),
      replySettings: z.string().optional(),
      metadata: z.record(z.string(), z.unknown()).optional(),
      mediaMetadata: z.array(z.unknown()).optional(),
    })
    .passthrough()
);

export const TwitterApiTweetSchema = TwitterApiTweetSchemaInternal;
